import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { PageHeader } from '../components/PageHeader';
import { CheckIcon, MapPinIcon, TruckIcon, SmileIcon } from 'lucide-react';
interface HotelRentalsPageProps {
  onNavigate: (page: string) => void;
}
export function HotelRentalsPage({
  onNavigate
}: HotelRentalsPageProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const benefits = ['Free hotel delivery and pickup', 'All major hotels in the DMV covered', 'Available for business & leisure travelers', '100% Satisfaction guaranteed'];
  const steps = [{
    icon: MapPinIcon,
    title: 'Tell Us Your Hotel',
    desc: 'Provide us with your hotel name and address when making your reservation.'
  }, {
    icon: TruckIcon,
    title: 'We Deliver',
    desc: 'We deliver the equipment directly to your hotel doorstep or bell desk before you arrive.'
  }, {
    icon: SmileIcon,
    title: 'Enjoy Your Stay',
    desc: 'Experience the city comfortably. Leave the equipment at the bell desk when you depart.'
  }];
  return <main className="flex-grow w-full bg-white" data-id="element-1076">
      <PageHeader title="Hotel Rentals" breadcrumb="Hotel Rentals" onNavigateHome={() => onNavigate('home')} data-id="element-1077" />

      <section className="py-20 overflow-hidden" data-id="element-1078">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1079">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-24" data-id="element-1080">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-1081">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-brand-navy mb-6 leading-tight" data-id="element-1082">
                Mobility Equipment Delivered to Your Hotel
              </h2>

              <div className="space-y-4 text-brand-charcoal/80 text-lg leading-relaxed mb-8" data-id="element-1083">
                <p data-id="element-1084">
                  Welcome to WheelAround Rentals, where we provide convenient
                  and reliable mobility solutions for travelers. We understand
                  that when traveling, mobility can be a concern, and that's
                  where we come in.
                </p>
                <p data-id="element-1085">
                  Our rental services provide a wide selection of electric
                  scooters, manual wheelchairs, power wheelchairs, and other
                  mobility aids to suit your needs. Our rental services cover
                  Washington D.C., Maryland, Baltimore, and Northern Virginia,
                  and we offer delivery services to various hotels in these
                  areas.
                </p>
                <p data-id="element-1086">
                  All you need to do is let us know your hotel's name and
                  address, and we will deliver the equipment to your doorstep.
                  Our rental services are available to all hotel guests, whether
                  you're in town for business or leisure.
                </p>
                <p data-id="element-1087">
                  We provide exceptional customer service and a satisfaction
                  guarantee to ensure that your rental experience with us is
                  hassle-free and comfortable. Trust us to provide you with
                  quality service and equipment during your stay.
                </p>
              </div>

              <ul className="space-y-4 mb-10" data-id="element-1088">
                {benefits.map((benefit, index) => <motion.li key={index} initial={{
                opacity: 0,
                x: 20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: 0.3 + index * 0.1
              }} className="flex items-center gap-3" data-id="element-1089">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-red/10 flex items-center justify-center" data-id="element-1090">
                      <CheckIcon className="w-4 h-4 text-brand-red font-bold" data-id="element-1091" />
                    </div>
                    <span className="text-brand-navy font-medium text-lg" data-id="element-1092">
                      {benefit}
                    </span>
                  </motion.li>)}
              </ul>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="relative" data-id="element-1093">
              <div className="absolute inset-0 sm:-inset-4 bg-brand-navy/5 rounded-[2.5rem] transform -rotate-2 -z-10" data-id="element-1094"></div>
              <img src="https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" alt="Luxury Hotel Entrance" className="rounded-3xl shadow-2xl object-cover w-full h-[600px]" data-id="element-1095" />
              <div className="absolute -bottom-3 right-2 sm:-bottom-6 sm:-right-6 bg-brand-navy text-white p-4 sm:p-6 rounded-2xl shadow-xl max-w-[200px]" data-id="element-1096">
                <div className="text-3xl font-bold text-brand-red mb-1" data-id="element-1097">
                  100%
                </div>
                <div className="text-sm font-medium" data-id="element-1098">
                  Free Delivery to Your Hotel Doorstep
                </div>
              </div>
            </motion.div>
          </div>

          {/* How It Works */}
          <div className="bg-brand-gray rounded-3xl p-10 md:p-16" data-id="element-1099">
            <div className="text-center mb-12" data-id="element-1100">
              <h2 className="text-3xl font-bold text-brand-navy mb-4" data-id="element-1101">
                How Hotel Delivery Works
              </h2>
              <p className="text-lg text-brand-charcoal/70" data-id="element-1102">
                Experience the convenience and accessibility of our Hotel
                Rentals and make your stay in the city comfortable and
                enjoyable.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1103">
              {steps.map((step, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.2
            }} className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 text-center" data-id="element-1104">
                  <div className="w-16 h-16 mx-auto bg-brand-blue/10 rounded-full flex items-center justify-center text-brand-blue mb-6" data-id="element-1105">
                    <step.icon className="w-8 h-8" data-id="element-1106" />
                  </div>
                  <h3 className="text-xl font-bold text-brand-navy mb-3" data-id="element-1107">
                    {step.title}
                  </h3>
                  <p className="text-brand-charcoal/70" data-id="element-1108">{step.desc}</p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 bg-brand-navy text-center" data-id="element-1109">
        <div className="max-w-4xl mx-auto px-4" data-id="element-1110">
          <h2 className="text-3xl font-bold text-white mb-6" data-id="element-1111">
            Ready to book your hotel delivery?
          </h2>
          <p className="text-lg text-white/80 mb-10" data-id="element-1112">
            Contact us now to learn more about our rental services and how we
            can assist you with your mobility needs during your stay.
          </p>
          <button onClick={() => onNavigate('reserve')} className="px-8 py-4 bg-brand-red text-white rounded-full font-bold text-lg hover:bg-red-700 transition-all shadow-lg" data-id="element-1113">
            Book Hotel Delivery
          </button>
        </div>
      </section>
    </main>;
}